import {
  AccountBalance,
  ArrowBack as ArrowBackIcon,
  RequestQuote,
  Schedule,
} from "@mui/icons-material";
import { Alert, Button, Grid, Snackbar } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAppContextController } from "context/AppContext";
import { EventContextProvider } from "context/EventContext";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useCompanyCache from "hooks/useCompanyCache";
import useGetBackButton from "hooks/useGetBackButton";
import useHandleBackButtonMainPage from "hooks/useHandleBackButtonMainPage";
import useVenueCache from "hooks/useVenueCache";
import EventsPreviewModal from "layouts/pages/events/components/EventsPreviewModal";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useQuery, useQueryClient } from "react-query";
import generateFiltersFromString from "utils/generateFiltersFromString";
import { checkIfApplicantVenuePending } from "utils/helpers/applicant";
import { getPeriodForDate } from "utils/helpers/payrollHelper";
import { isEmployeeUser } from "utils/helpers/roles";
import CompanyJobInfoPanel from "../companyjobs/components/CompanyJobInfoPanel";
import CompanyJobPreview from "../companyjobs/components/CompanyJobPreview";
import NewEventInfoPanel from "../events/components/NewEventInfoPanel";
import JobsInformation from "../jobs/components/JobsInformation";
import JobsModal from "../jobs/components/JobsModal";
import calculateBurdenRate from "./actions/calculateBurdenRate";
import InvoiceEditorTable from "./components/InvoiceEditorTable";
import PayrollEditorTable from "./components/PayrollEditorTable";
import PayrollFilterCards from "./components/PayrollFilterCards";
import PayrollStatusFilterCards from "./components/PayrollStatusFilterCards";
import PayrollTable from "./components/PayrollTable";
import TimecardEditorTable from "./components/TimecardEditorTable";

function Payroll() {
  const {
    currentEvent,
    setCurrentEvent,
    currentJob,
    setCurrentJob,
    currentLoggedUser,
    venues,
    setVenues,
    userType,
    isMobile,
    currentApplicant,
    company,
    setCompany,
    setCompanyType,
  } = useAppContextController();
  const { isLoadingCompany, refetchCompany } = useCompanyCache({
    company,
    setCompany,
    setCompanyType,
  });
  const { isLoadingVenues, refetchVenues } = useVenueCache({ venues, setVenues, company });
  const { enqueueSnackbar } = useSnackbar();

  const refetchEventsFuncRef = useRef(null);

  const bindRefetchEvents = (callback) => {
    refetchEventsFuncRef.current = callback;
  };

  const isProduction = process.env.REACT_APP_ENV?.trim() === "production";
  const shouldShowRestrictedEditors =
    company?.payrollAuthorizedEmails?.includes(currentLoggedUser?.emailAddress) || !isProduction;

  const refetchEvents = refetchEventsFuncRef.current;

  const isNotUser = userType !== "User";
  const isEventAdmin = currentLoggedUser?.employeeType === "Event Admin" && userType === "User";

  const [searchParams] = useSearchParams();
  const myVenues = searchParams.get("myVenues");
  const entityType = searchParams.get("entityType");
  const panel = searchParams.get("panel");
  const venueSlug = searchParams.get("venueSlug");
  const companySlug = searchParams.get("companySlug");
  const periodDate = searchParams.get("periodDate");
  const filterCardVenues = currentLoggedUser?.venues?.length
    ? currentLoggedUser.venues.join(";")
    : "";

  const startOfPeriodDay = company?.payrollSettings?.payrollStartOfPeriod ?? 1;
  const periodLength = company?.payrollSettings?.payrollPeriodLength || "weekly";
  const shiftPeriodWeek = (company?.payrollSettings?.payrollShiftPeriodWeek || "No") === "Yes";

  const initialPeriod = useMemo(() => {
    const todayMoment = moment(periodDate || undefined);

    return getPeriodForDate(
      todayMoment.toISOString(),
      periodLength,
      shiftPeriodWeek,
      startOfPeriodDay
    );
  }, [periodDate, periodLength, shiftPeriodWeek, startOfPeriodDay]);

  const [currentPeriod, setCurrentPeriod] = useState(initialPeriod);

  // Handle back button for this screen
  const {
    backListUrl,
    filterParam,
    pageParam,
    subFilterParam,
    subPageParam,
    pageFromUrl,
    filtersFromUrl,
    subPageFromUrl,
    subFiltersFromUrl,
    navigateToListWithBack,
    navigateToScreenWithBack,
  } = useHandleBackButtonMainPage({
    baseUrl: "/payroll",
    screenName: "Payroll",
  });

  // Get back button generated by other screens
  const { backParam, backToTitle, backToUrl, backButton } = useGetBackButton();

  const eventListNavigateFuncRef = useRef(null);
  const bindEventListNavigateFunc = (func) => {
    eventListNavigateFuncRef.current = func;
  };

  const queryClient = useQueryClient();
  const isEdit = !!currentEvent?._id;

  const { eventUrl, jobSlug, action } = useParams();
  const [open, setOpen] = useState(false);
  const [eventPreview, setEventPreview] = useState(null);
  const [jobPreview, setJobPreview] = useState(null);

  const [filters, setFilters] = useState({
    entityType: entityType ?? (company?.companyType === "Venue" ? "Event" : "Job"),
  });
  const [actionSelected, setActionSelected] = useState(action || "");
  const [exportFieldModalOpen, setExportFieldModalOpen] = useState(false);
  const [exportRosterStatus, setExportRosterStatus] = useState(null);
  const [isMessageModalOpen, toggleMessageModal] = useState(false);
  const [applicantsData, setApplicantsData] = useState([]);
  const [rosterData, setRosterData] = useState([]);
  const [partnerRosterData, setPartnerRosterData] = useState([]);
  const [previewContent, setPreviewContent] = useState("all");
  const [selectedPanel, setSelectedPanel] = useState(panel ?? "timecard");
  const [notesModalOpen, setNoteModalOpen] = useState(false);
  const [toastAlert, setToastAlert] = useState({
    isShow: false,
    message: "",
  });

  useEffect(() => {
    if (panel) setSelectedPanel(panel);
  }, [panel]);

  const navigate = useNavigate();

  const getVenueField = useCallback(
    (slug, field) => {
      if (venues && venues[slug]) return venues[slug][field];
      return null;
    },
    [venues]
  );

  const isApplicantEventVenuePending = useMemo(() => {
    const eventVenueSlug = eventPreview?.venueSlug;
    const applicantVenues = currentApplicant?.venues;
    const isPending = checkIfApplicantVenuePending(applicantVenues, eventVenueSlug);
    return isPending;
  }, [eventPreview, currentApplicant]);

  const toggleEventPreviewModal = (isOpen) => {
    if (isApplicantEventVenuePending && isEmployeeUser(userType)) {
      toggleMessageModal(true);
    } else {
      setOpen(isOpen);
    }
  };

  const burdenRateOptions = { eventUrl, jobSlug };

  const {
    data: burdenRate,
    isLoading: isLoadingBurdenRate,
    refetch: refetchBurdenRate,
  } = useQuery(
    ["payrollBurdenRate", burdenRateOptions],
    () => calculateBurdenRate(burdenRateOptions),
    {
      refetchOnWindowFocus: false,
      enabled: !!eventUrl || !!jobSlug,
    }
  );

  const renderPanel = (selection) => {
    switch (selection) {
      // case "activities":
      //   return <EventActivities />;
      case "timecard":
        return (
          <TimecardEditorTable
            title={`Timecard Editor for: ${currentEvent?.eventName || currentJob?.title}`}
            navigateToAction="employeeinfo"
            pageSize={50}
            parentPage={subPageFromUrl}
            parentFilters={subFiltersFromUrl}
            parentNavigateWithBack={navigateToScreenWithBack}
            currentPeriod={currentPeriod}
            burdenRate={burdenRate}
            isLoadingBurdenRate={isLoadingBurdenRate}
          />
        );
      case "payroll":
        return (
          <PayrollEditorTable
            title={`Payroll Editor for: ${currentEvent?.eventName || currentJob?.title}`}
            navigateToAction="employeeinfo"
            pageSize={50}
            parentPage={subPageFromUrl}
            parentFilters={subFiltersFromUrl}
            parentNavigateWithBack={navigateToScreenWithBack}
            currentPeriod={currentPeriod}
            burdenRate={burdenRate}
            isLoadingBurdenRate={isLoadingBurdenRate}
          />
        );
      case "invoice":
        return (
          <InvoiceEditorTable
            title={`Invoice Editor for: ${currentEvent?.eventName || currentJob?.title}`}
            navigateToAction="employeeinfo"
            pageSize={50}
            parentPage={subPageFromUrl}
            parentFilters={subFiltersFromUrl}
            parentNavigateWithBack={navigateToScreenWithBack}
            currentPeriod={currentPeriod}
            burdenRate={burdenRate}
            isLoadingBurdenRate={isLoadingBurdenRate}
          />
        );
      // case "additionalInfo":
      //   return <AdditionalInformation setToastAlert={setToastAlert} />;
      // case "content":
      //   return (
      //     <EventContent
      //       setOpen={toggleEventPreviewModal}
      //       setEventPreview={setEventPreview}
      //       setPreviewContent={setPreviewContent}
      //     />
      //   );
      // case "attachments":
      //   return <EventAttachments />;
      // case "additionalpositions":
      //   return <EventAdditionalPositions />;
      // case "settings":
      //   return <EventSettings />;
      // case "notes":
      //   return <EventNotes setOpen={setNoteModalOpen} currentEvent={currentEvent} />;
      // case "geofencing":
      //   return <EventGeofencing getVenueField={getVenueField} />;
      // case "reportdata":
      //   return <EventReportData />;
      default:
        return null;
    }
  };

  const panelOptions = [
    {
      title: "Timecard Editor",
      icon: <Schedule />,
      hasShadow: selectedPanel === "timecard" ? "emd" : "",
      color: "error",
      ariaLabel: "timecard",
      fn: () => setSelectedPanel("timecard"),
      name: "timecard",
    },
    ...(shouldShowRestrictedEditors
      ? [
          {
            title: "Payroll Editor",
            icon: <RequestQuote />,
            hasShadow: selectedPanel === "payroll" ? "emd" : "",
            color: "success",
            ariaLabel: "payroll",
            fn: () => setSelectedPanel("payroll"),
            name: "payroll",
          },
          {
            title: "Invoice Editor",
            icon: <AccountBalance />,
            hasShadow: selectedPanel === "invoice" ? "emd" : "",
            color: "violet",
            ariaLabel: "invoice",
            fn: () => setSelectedPanel("invoice"),
            name: "invoice",
          },
        ]
      : []),
  ];

  useEffect(() => {
    // this check added as default added when company loaded
    if (isLoadingCompany) return;

    let newFilters = { ...filters };
    if (venueSlug) {
      newFilters = {
        ...newFilters,
        venueSlug,
      };
    }
    if (companySlug) {
      newFilters = {
        ...newFilters,
        companySlug,
      };
    }
    const tempEntityType = filters?.entityType || entityType;
    if (tempEntityType) {
      newFilters = {
        ...newFilters,
        entityType: tempEntityType,
      };
    }
    if (eventUrl && action) {
      newFilters = { ...newFilters, eventUrl };
    }
    if (jobSlug && action) {
      newFilters = { ...newFilters, jobSlug };
    }
    if (newFilters?.eventUrl && filters?.entityType === "Job") {
      newFilters = { ...newFilters, entityType: "Event" };
    }
    if (newFilters?.jobSlug && filters?.entityType === "Event") {
      newFilters = { ...newFilters, entityType: "Job" };
    }
    setFilters(newFilters);
    if (action) {
      setActionSelected(action);
    } else {
      setActionSelected("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, entityType, eventUrl, jobSlug, isLoadingCompany, venueSlug, companySlug]);

  useEffect(() => {
    if (filterParam != null) {
      if (filterParam) {
        const newFilters = generateFiltersFromString(filterParam);
        setFilters(newFilters);
      } else {
        setFilters({});
      }
    }
  }, [filterParam]);

  const setNavigateUrl = (url) => navigate(url);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        open={toastAlert.isShow}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setToastAlert({ isShow: false, message: "", status: "" })}
        key="top-center"
      >
        <Alert
          onClose={() => setToastAlert({ isShow: false, message: "", status: "" })}
          // severity={toastAlert.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastAlert.message}
        </Alert>
      </Snackbar>
      <EventContextProvider>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            {backToUrl ? (
              <Grid item xs={12} sm={12} md={12} pb={5} pt={3}>
                {backButton}
              </Grid>
            ) : (
              <>
                {company?.companyType === "Venue" && (
                  <PayrollFilterCards
                    filters={filters}
                    setFilters={setFilters}
                    currentPeriod={currentPeriod}
                  />
                )}
                <PayrollStatusFilterCards
                  filters={filters}
                  setFilters={setFilters}
                  currentPeriod={currentPeriod}
                />
              </>
            )}
            <PayrollTable
              eventUrl={eventUrl}
              setActionSelected={setActionSelected}
              showFiltersList
              filters={filters}
              title="Payroll"
              setFilters={setFilters}
              setNavigateUrl={setNavigateUrl}
              defaultSort="eventDate"
              // setOpen={setOpen}
              parent="Payroll"
              setOpen={toggleEventPreviewModal}
              setEventPreview={setEventPreview}
              setJobPreview={setJobPreview}
              setPreviewContent={setPreviewContent}
              bindRefetchEvents={bindRefetchEvents}
              setToastAlert={setToastAlert}
              parentPage={pageFromUrl}
              parentNavigateWithBack={navigateToListWithBack}
              bindNavigateFunc={bindEventListNavigateFunc}
              currentPeriod={currentPeriod}
              setCurrentPeriod={setCurrentPeriod}
            />
          </Grid>
          {backListUrl && (
            <Grid item xs={12} sm={12} md={12} mt={2}>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  navigate(backListUrl);
                }}
              >
                Back to Payroll List
              </Button>
            </Grid>
          )}
          {["eventsinfo", "payrollall", ""].includes(actionSelected) &&
            company?.companyType === "Venue" &&
            !!eventUrl && (
              <>
                <Grid item xs={12} sm={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={12} md={12} lg={6}>
                      <NewEventInfoPanel
                        setOpen={toggleEventPreviewModal}
                        setEventPreview={setEventPreview}
                        setPreviewContent={setPreviewContent}
                        selectedPanel={selectedPanel}
                        onPanelSelect={setSelectedPanel}
                        getVenueField={getVenueField}
                        overridePanelOptions={panelOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={6}>
                      {renderPanel(selectedPanel)}
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
          {["jobinfo", "payrollall", ""].includes(actionSelected) &&
            company?.companyType === "Venue" &&
            !!jobSlug && (
              <Grid item xs={12} sm={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <JobsInformation
                      mode="edit"
                      setRefetchJobs={() => {}}
                      setOpen={setOpen}
                      setJobPreview={setJobPreview}
                      setToastAlert={setToastAlert}
                      selectedPanel={selectedPanel}
                      onPanelSelect={setSelectedPanel}
                      overridePanelOptions={panelOptions}
                      addExtraMargin
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    {renderPanel(selectedPanel)}
                  </Grid>
                </Grid>
              </Grid>
            )}
          {["jobinfo", "payrollall", ""].includes(actionSelected) &&
            company?.companyType !== "Venue" &&
            !!jobSlug && (
              <Grid item xs={12} sm={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <CompanyJobInfoPanel
                      setRefetchJobs={() => {}}
                      setPreviewContent={setPreviewContent}
                      previewContent={previewContent}
                      selectedPanel={selectedPanel}
                      onPanelSelect={setSelectedPanel}
                      overridePanelOptions={panelOptions}
                      addExtraMargin
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    {renderPanel(selectedPanel)}
                  </Grid>
                </Grid>
              </Grid>
            )}
          {["timecard", "payrollall"].includes(actionSelected) && (!!eventUrl || !!jobSlug) && (
            <>
              <Grid item xs={12} sm={12} md={12}>
                <TimecardEditorTable
                  title={`Timecard Editor for: ${currentEvent?.eventName || currentJob?.title}`}
                  navigateToAction="employeeinfo"
                  pageSize={50}
                  parentPage={subPageFromUrl}
                  parentFilters={subFiltersFromUrl}
                  parentNavigateWithBack={navigateToScreenWithBack}
                  currentPeriod={currentPeriod}
                  fullscreen
                  panelOptions={panelOptions}
                  burdenRate={burdenRate}
                  isLoadingBurdenRate={isLoadingBurdenRate}
                />
              </Grid>
            </>
          )}
          {["payroll", "payrollall"].includes(actionSelected) &&
            (!!eventUrl || !!jobSlug) &&
            shouldShowRestrictedEditors && (
              <>
                <Grid item xs={12} sm={12} md={12}>
                  <PayrollEditorTable
                    title={`Payroll Editor for: ${currentEvent?.eventName || currentJob?.title}`}
                    navigateToAction="employeeinfo"
                    pageSize={50}
                    parentPage={subPageFromUrl}
                    parentFilters={subFiltersFromUrl}
                    parentNavigateWithBack={navigateToScreenWithBack}
                    currentPeriod={currentPeriod}
                    fullscreen
                    panelOptions={panelOptions}
                    burdenRate={burdenRate}
                    isLoadingBurdenRate={isLoadingBurdenRate}
                  />
                </Grid>
              </>
            )}
          {["invoice", "payrollall"].includes(actionSelected) &&
            (!!eventUrl || !!jobSlug) &&
            shouldShowRestrictedEditors && (
              <>
                <Grid item xs={12} sm={12} md={12}>
                  <InvoiceEditorTable
                    title={`Invoice Editor for: ${currentEvent?.eventName || currentJob?.title}`}
                    navigateToAction="employeeinfo"
                    pageSize={50}
                    parentPage={subPageFromUrl}
                    parentFilters={subFiltersFromUrl}
                    parentNavigateWithBack={navigateToScreenWithBack}
                    currentPeriod={currentPeriod}
                    fullscreen
                    panelOptions={panelOptions}
                    burdenRate={burdenRate}
                    isLoadingBurdenRate={isLoadingBurdenRate}
                  />
                </Grid>
              </>
            )}
          {!!eventPreview && (
            <EventsPreviewModal
              open={!!eventPreview}
              setOpen={() => setEventPreview(null)}
              eventPreview={eventPreview}
              setEventPreview={setEventPreview}
              content={previewContent}
              refetchEvents={refetchEvents}
              navigateWithBack={eventListNavigateFuncRef.current}
            />
          )}
          {!!jobPreview && company?.companyType === "Venue" && (
            <JobsModal
              open={!!jobPreview}
              setOpen={() => setJobPreview(null)}
              jobPreview={jobPreview}
            />
          )}
          {!!jobPreview && company?.companyType === "Company" && (
            <CompanyJobPreview
              content="all"
              open={!!jobPreview}
              setOpen={() => setEventPreview(null)}
              closeHandler={() => setEventPreview(null)}
              job={jobPreview}
              customer={jobPreview}
            />
          )}
        </Grid>
      </EventContextProvider>
    </DashboardLayout>
  );
}

// Setting default values for the props of BaseLayout
// Events.defaultProps = {
//   pageName: "Events",
// };

// // Typechecking props for BaseLayout
// Events.propTypes = {
//   pageName: PropTypes.string,
// };

export default Payroll;
